import { FlexBox, Icon, Page, Tabs } from '@dixa/lib-shared-habla';
import { UserRole } from 'lib-shared-user-role';
import { matchRoutes, useLocation } from 'react-router-dom';

import { isPWA } from 'app/middleware/visibility/utils/helpers';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { AppPathWithoutParams } from 'utils/url/appPaths';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useCurrentUser, userHasRole } from 'utils/user';

type IntegrationTab = {
  readonly id: string;
  readonly label: string;
  readonly path: AppPathWithoutParams;
  readonly minimumRole?: UserRole;
};

const integrationsTabs: readonly IntegrationTab[] = [
  {
    id: 'conversationCards',
    label: 'Conversation cards',
    path: '/settings/integrations',
    minimumRole: 'admin',
  },
  {
    id: 'apiTokens',
    label: 'API tokens',
    path: '/settings/integrations/api-tokens',
    minimumRole: 'admin',
  },
  {
    id: 'webhooks',
    label: 'Webhooks',
    path: '/settings/integrations/webhooks',
    minimumRole: 'admin',
  },
  {
    id: 'webformEndpoints',
    label: 'Webform endpoints',
    path: '/settings/integrations/webform-endpoints',
    minimumRole: 'admin',
  },
  {
    id: 'chatbots',
    label: 'Chatbots',
    path: '/settings/integrations/chatbots',
    minimumRole: 'admin',
  },
  {
    id: 'busylight',
    label: 'Busylight',
    path: '/settings/integrations/busylight',
  },
  {
    id: 'jabra',
    label: 'Jabra',
    path: '/settings/integrations/jabra',
  },
];

/** Displays the header for settings/integrations pages. */
export const SettingsIntegrationsPageHeader = (): JSX.Element => {
  const location = useLocation();
  const navigate = useAppNavigate();
  const { user } = useCurrentUser();
  const { webUsbJabraIntegration, personalSettingsRevamp } = useFeatureFlag([
    'web-usb-jabra-integration',
    'personal-settings-revamp',
  ]);

  const accessibleTabs = integrationsTabs.filter((route) => {
    if (route.id === 'jabra') {
      return webUsbJabraIntegration && isPWA();
    }

    if (route.id === 'busylight' && personalSettingsRevamp) {
      return false;
    }

    return route?.minimumRole ? userHasRole(user, route.minimumRole) : true;
  });

  // Add wildcard after each path to cover all subroutes
  const routes = accessibleTabs.map((item) => ({
    ...item,
    path: `${item.path}/*`,
  }));

  const matches = matchRoutes(routes, location);
  const currentRoute = matches?.[0]?.route;

  const handleTabChange = (id: string) => {
    const tabToGo = accessibleTabs.find((item) => item.id === id)?.path;

    navigate({
      path: tabToGo as AppPathWithoutParams,
    });
  };

  return (
    <>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            Integrations
            {accessibleTabs.length === 1 && (
              <>
                <Icon name="angle-right" />
                {accessibleTabs[0].label}
              </>
            )}
          </FlexBox>
        }
      />

      {accessibleTabs.length > 1 && (
        <Page.Tabs>
          <Tabs
            selectedId={currentRoute?.id}
            tabs={accessibleTabs}
            onChange={(id) => handleTabChange(id)}
          />
        </Page.Tabs>
      )}
    </>
  );
};
